import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { setPageMeta } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Minus, Plus, ShoppingCart, Check, ArrowLeft, Ruler, Palette, Layers, FileText, Lightbulb, Images } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [color, setColor] = useState("");
  const [variant, setVariant] = useState("");
  const [qty, setQty] = useState(1);
  const [example, setExample] = useState("2photos");

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setColor((data.colors || [])[0] || "");
      setVariant((data.variants || [])[0]?.name || "");
      setPageMeta(`${data.name} — Tenay Ain-Pression`, data.description);
    }).catch(() => toast.error("Produit introuvable")).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const imgs = product?.images || [];
    if (imgs.length < 2) return;
    const t = setInterval(() => setActiveImg((i) => (i + 1) % imgs.length), 5000);
    return () => clearInterval(t);
  }, [product]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E6007E]" /></div>;
  if (!product) return <div className="py-32 text-center text-gray-400">Produit introuvable. <Link to="/boutique" className="text-[#E6007E]">Retour boutique</Link></div>;

  const vObj = (product.variants || []).find((v) => v.name === variant);
  const unitPrice = product.price + (vObj?.price_delta || 0);
  const images = product.images && product.images.length ? product.images : ["/images/custom_object.jpeg"];

  const add = () => {
    addItem(product, { quantity: qty, color, variant, unitPrice });
    toast.success("Ajouté au panier");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <div className="rounded-2xl overflow-hidden border border-[#222] bg-black aspect-square relative">
            {images.map((im, i) => (
              <div key={i} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: activeImg === i ? 1 : 0 }}>
                <img src={im} alt={product.name} className="w-full h-full object-cover"
                  data-testid={i === 0 ? "product-main-image" : undefined} />
              </div>
            ))}
          </div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-[#E6007E]" : "border-transparent"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider text-[#0055FF] font-bold">{product.material}</p>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black" data-testid="product-title">{product.name}</h1>
          {product.quote_only
            ? <p className="mt-4 text-3xl font-black text-[#E6007E]" data-testid="product-price">À partir de {product.price.toFixed(2)} €</p>
            : <p className="mt-4 text-3xl font-black text-[#E6007E]" data-testid="product-price">{unitPrice.toFixed(2)} €</p>}
          <p className="mt-5 text-gray-300 leading-relaxed">{product.description}</p>

          {product.colors?.length > 0 && (
            <div className="mt-7">
              <p className="flex items-center gap-2 text-sm font-bold text-white mb-3"><Palette className="w-4 h-4 text-[#E6007E]" /> Couleur</p>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button key={c} data-testid={`color-${c}`} onClick={() => setColor(c)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${color === c ? "bg-[#E6007E] border-[#E6007E]" : "border-white/20 hover:border-[#E6007E]"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.variants?.length > 0 && (
            <div className="mt-6">
              <p className="flex items-center gap-2 text-sm font-bold text-white mb-3"><Layers className="w-4 h-4 text-[#0055FF]" /> Variante</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <button key={v.name} data-testid={`variant-${v.name}`} onClick={() => setVariant(v.name)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${variant === v.name ? "bg-[#0055FF] border-[#0055FF]" : "border-white/20 hover:border-[#0055FF]"}`}>
                    {v.name}{v.price_delta ? ` (+${v.price_delta}€)` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.dimensions?.length > 0 && (
            <div className="mt-6">
              <p className="flex items-center gap-2 text-sm font-bold text-white mb-2"><Ruler className="w-4 h-4 text-[#E6007E]" /> Dimensions</p>
              <p className="text-gray-300">{product.dimensions.join(" · ")}</p>
            </div>
          )}

          {product.quote_only ? (
            <div className="mt-8">
              <Link
                to={`/devis?category=${encodeURIComponent(product.category)}&subject=${encodeURIComponent(product.name)}`}
                data-testid="request-quote-btn"
                className="btn-primary rounded-full px-8 py-4 text-base font-bold inline-flex items-center justify-center w-full sm:w-auto"
              >
                <FileText className="w-5 h-5 mr-2" /> Demander un devis
              </Link>
              <p className="mt-3 text-sm text-gray-400">Tarif établi selon le volume et la complexité de votre figurine.</p>
            </div>
          ) : (
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center rounded-full border border-white/20">
                <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:text-[#E6007E]"><Minus className="w-4 h-4" /></button>
                <span className="w-10 text-center font-bold" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="p-3 hover:text-[#E6007E]"><Plus className="w-4 h-4" /></button>
              </div>
              <Button data-testid="add-to-cart-btn" onClick={add} className="btn-primary rounded-full px-8 py-6 text-base font-bold flex-1">
                <ShoppingCart className="w-5 h-5 mr-2" /> Ajouter au panier
              </Button>
            </div>
          )}

          <div className="mt-6 rounded-xl border border-[#0055FF]/40 bg-[#0055FF]/10 p-4 text-sm text-gray-300 flex items-start gap-3">
            <Check className="w-5 h-5 text-[#0055FF] shrink-0" />
            Besoin d'une personnalisation particulière (couleur, texte, dimensions) ? <Link to="/devis" className="text-[#0055FF] font-bold ml-1">Demandez un devis</Link>.
          </div>
        </div>
      </div>

      {product.category === "lithophanies" && (
        <section data-testid="litho-explainer" className="mt-16 rounded-2xl border border-[#222] bg-[#0A0A0A] p-6 sm:p-10">
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-[#E6007E]/15 p-3"><Lightbulb className="w-6 h-6 text-[#E6007E]" /></span>
            <h2 className="font-heading text-2xl sm:text-3xl font-black">Comment on crée votre lithophanie</h2>
          </div>
          <div className="mt-5 grid gap-4 text-gray-300 leading-relaxed max-w-3xl">
            <p>
              Une <strong className="text-white">lithophanie</strong> est une image gravée en relief dans une fine paroi imprimée en 3D.
              Là où la matière est plus fine, la lumière passe davantage ; là où elle est plus épaisse, elle s'assombrit.
              Résultat : une fois éclairée par l'arrière, votre photo apparaît avec une profondeur et une émotion incroyables.
            </p>
            <p>
              Vous nous envoyez une <strong className="text-white">photo nette et bien contrastée</strong> (ou plusieurs à fusionner).
              Nous nous occupons de la conversion, du calibrage des épaisseurs et de l'impression, en PLA ou PETG.
              Éteinte, la pièce reste discrète et élégante ; allumée, elle prend vie.
            </p>
          </div>

          <div className="mt-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="flex items-center gap-2 text-sm font-bold text-white"><Images className="w-4 h-4 text-[#0055FF]" /> Exemples de création :</span>
              <Select value={example} onValueChange={setExample}>
                <SelectTrigger data-testid="litho-example-select" className="bg-black border-white/15 w-full sm:w-80">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2photos">Fusion de 2 photos — « 30 ans de décalage »</SelectItem>
                  <SelectItem value="3photos">Fusion de 3 photos — « Les Lithophanies »</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mt-5 rounded-2xl overflow-hidden border border-[#222] bg-black max-w-3xl">
              <img
                data-testid="litho-example-image"
                src={example === "2photos" ? "/images/litho_explainer_2photos.jpg" : "/images/litho_explainer_3photos.jpg"}
                alt={example === "2photos" ? "Exemple lithophanie : fusion de 2 photos" : "Exemple lithophanie : fusion de 3 photos"}
                className="w-full h-auto"
              />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
